import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { MoneyCell } from '@/components/composed/MoneyCell';
import { DateCell } from '@/components/composed/DateCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  InvoiceTimeline,
  VoidInvoiceConfirm,
  invoiceDetailQuery,
} from '@/features/invoices';
import { invoiceStatusMeta } from '@/features/invoices/invoice-status';
import { canEdit } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { invoiceId } from '@/lib/api/ids';

export const Route = createFileRoute('/_auth/invoices/$invoiceId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      invoiceDetailQuery(invoiceId(params.invoiceId)),
    ),
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const { invoiceId: invoiceIdParam } = Route.useParams();
  const id = invoiceId(invoiceIdParam);
  const { data: invoice } = useSuspenseQuery(invoiceDetailQuery(id));
  const session = useAuthStore((s) => s.session);
  const [voidOpen, setVoidOpen] = useState(false);

  usePageMeta({ title: `Invoice ${invoice.number}` });

  const meta = invoiceStatusMeta(invoice.status);
  const canVoid =
    canEdit(session) && invoice.status !== 'void' && invoice.status !== 'paid';

  return (
    <Page>
      <PageHeader
        title={invoice.number}
        description={invoice.clientName}
        actions={
          canVoid ? (
            <Button variant="destructive" onClick={() => setVoidOpen(true)}>
              <XCircle className="size-4" />
              Void
            </Button>
          ) : null
        }
      />

      <Section>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              Summary
              <StatusBadge label={meta.label} tone={meta.tone} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Amount</dt>
                <dd className="font-medium">
                  <MoneyCell
                    amountMinor={invoice.amountMinor}
                    currency={invoice.currency}
                  />
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Issued</dt>
                <dd className="font-medium">
                  <DateCell value={invoice.issuedAt} />
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Due</dt>
                <dd className="font-medium">
                  <DateCell value={invoice.dueAt} variant="calendar-day" />
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Client</dt>
                <dd className="font-medium">{invoice.clientName}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceTimeline invoiceId={id} />
          </CardContent>
        </Card>
      </Section>

      <VoidInvoiceConfirm
        invoiceId={id}
        invoiceNumber={invoice.number}
        open={voidOpen}
        onOpenChange={setVoidOpen}
      />
    </Page>
  );
}
